import React, { useCallback, useContext, useMemo } from 'react'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import OfferFilterDropdownBox from '../OfferFilterDropdownBox'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { countOccupants, formatOccupantsForSelectionBox } from 'lib/offer/occupancyUtils'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { isTourV2Offer } from 'lib/offer/offerTypes'
import { EmptyArray } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import { pluralizeToString } from 'lib/string/pluralize'
import { isSalesforceTourV2 } from 'lib/offer/offerUtils'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'
import { useIntl } from 'react-intl'

interface Props {
  rooms?: Array<App.Occupants>
  open: boolean
  offer: App.Offer | App.BedbankOffer | App.Tours.TourV2Offer
  inSidebar?: boolean
  occupantUnit?: 'guest' | 'traveller'
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void
  multiRoomDisabled?: boolean
  iconDisabled?: boolean
  className?: string
  ref?: React.Ref<HTMLButtonElement>
}

function OfferCapacityWithAgesSelectBox(props: Props) {
  const {
    open,
    rooms = EmptyArray,
    offer,
    inSidebar,
    occupantUnit = 'guest',
    onClick = noop,
    multiRoomDisabled,
    iconDisabled,
    className,
    ref,
  } = props
  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const allRooms = useMemo(() => countOccupants(rooms), [rooms])
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const hasRooms = hasSelectedRooms(rooms)

  const onBoxClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    if (open) {
      dispatch({ type: OfferPageActions.closeFilter })
      trackEvent('OCCUPANCY_FILTERS_CLOSED')
    } else {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
      trackEvent('OCCUPANCY_FILTERS_PRESSED')
      onClick(e)
    }
  }, [open, onClick, dispatch, trackEvent])

  const showInvalid = useMemo(() => !open && allRooms.childrenAge?.some(age => age === -1), [allRooms, open])
  const value = useMemo(() => {
    if (hasRooms) {
      const occupantsText = formatOccupantsForSelectionBox(isTourV2Offer(offer) && isSalesforceTourV2(offer), allRooms)
      const roomText = multiRoomDisabled ? '' : pluralizeToString(offer.saleUnit, rooms.length)
      return <><b>{occupantsText}</b>{roomText ? ` (${roomText})` : undefined}</>
    }
  }, [allRooms, multiRoomDisabled, offer, rooms.length, hasRooms])

  const placeholderText = intl.formatMessage(
    {
      id: 'rU6LzVA30h',
      defaultMessage: 'Select {unit, select, guest {guests} traveller {travellers} other {guests}}',
      description: 'Placeholder text for occupancy selection dropdown',
    },
    { unit: occupantUnit },
  )

  return (
    <OfferFilterDropdownBox
      className={className}
      onClick={onBoxClick}
      active={open}
      ref={ref}
      invalid={showInvalid}
      invalidMessage={intl.formatMessage({
        id: 'se2qQS+6Xj',
        defaultMessage: 'Please select children’s ages',
        description: 'Validation error message when children ages are not selected',
      })}
      data-testid="OfferCapacitySelectBox"
      icon={!iconDisabled && <LineUserIcon />}
      inSidebar={inSidebar}
      label={intl.formatMessage({
        id: 'yW1w9zlaHb',
        defaultMessage: 'Guests',
        description: 'Label for occupancy selection dropdown',
      })}
      placeholder={placeholderText}
      value={value}
    />
  )
}

export default withOfferPageState(state => ({
  rooms: state.rooms,
  open: state.activeFilter === 'capacity',
  offer: state.offer ?? state.bedbankOffer ?? state.tourV2Offer,
}), OfferCapacityWithAgesSelectBox)
